import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Sparkles, GripVertical, Loader2, Layers, CheckCircle2, XCircle } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { useFiscalYear } from "@/context/FiscalYearContext";
import { PageHeader, Loading } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

function BasicTab() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/onboarding/settings").then(({ data }) => setS(data)); }, []);
  const save = async () => {
    try { await api.put("/onboarding/settings", s); toast.success("設定を保存しました"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  if (!s) return <Loading />;
  const toggles = [
    ["enabled", "入社研修機能を有効にする"], ["require_quiz", "理解度確認を必須にする"],
    ["require_reflection", "所感提出を必須にする"], ["require_orientation", "現場オリエンテーションを必須にする"],
    ["require_manager_confirm", "管理者最終確認を必須にする"], ["issue_certificate", "修了証を発行する"],
  ];
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 max-w-xl space-y-3">
      {toggles.map(([k, l]) => (
        <label key={k} className="flex items-center gap-2 text-sm text-slate-700 py-1 cursor-pointer">
          <Checkbox checked={!!s[k]} onCheckedChange={(c) => setS({ ...s, [k]: !!c })} data-testid={`onbset-${k}`} /> {l}
        </label>
      ))}
      <div className="pt-2"><Label>入職日からの標準受講期限（日数）</Label>
        <Input type="number" value={s.default_due_days} onChange={(e) => setS({ ...s, default_due_days: Number(e.target.value) })} className="mt-1 w-32" data-testid="onbset-duedays" />
      </div>
      <Button className="bg-blue-600 hover:bg-blue-700 mt-2" onClick={save} data-testid="onbset-save">保存</Button>
    </div>
  );
}

function TrainingSetTab() {
  const { fiscalYear } = useFiscalYear();
  const [items, setItems] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [form, setForm] = useState({ training_id: "", required: true, order: 0, due_days: "", reuse_existing: true, force_retake: false, status: "有効" });

  const load = useCallback(() => api.get("/onboarding/training-settings").then(({ data }) => setItems(data)), []);
  useEffect(() => { load(); }, [load]);

  const openAdd = async () => {
    const { data } = await api.get(`/onboarding/candidate-trainings${fiscalYear ? `?fiscal_year=${fiscalYear}` : ""}`);
    setCandidates(data); setForm({ training_id: "", required: true, order: (items?.length || 0), due_days: "", reuse_existing: true, force_retake: false, status: "有効" }); setAddOpen(true);
  };
  const add = async () => {
    if (!form.training_id) { toast.error("研修を選択してください"); return; }
    try { await api.post("/onboarding/training-settings", { ...form, due_days: form.due_days ? Number(form.due_days) : null }); toast.success("入社研修対象に追加しました"); setAddOpen(false); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const upd = async (it, patch) => {
    try { await api.put(`/onboarding/training-settings/${it.id}`, { ...it, ...patch, due_days: it.due_days ?? null }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const remove = async (it) => {
    if (!window.confirm(`「${it.training_name}」を入社研修対象から外しますか？`)) return;
    try { await api.delete(`/onboarding/training-settings/${it.id}`); toast.success("削除しました"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  if (!items) return <Loading />;

  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <p className="text-sm text-slate-500">全社共通の入社研修セットです。既存研修から対象を選択します。</p>
        <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={openAdd} data-testid="onbset-add-training"><Plus className="w-4 h-4 mr-1" />研修を追加</Button>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>研修名</TableHead><TableHead>事業所/年度</TableHead><TableHead>必須</TableHead><TableHead>期限日数</TableHead><TableHead>既存修了</TableHead><TableHead>再受講必須</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map((it) => (
              <TableRow key={it.id} data-testid={`onbset-tr-${it.id}`}>
                <TableCell className="font-medium text-slate-800">{it.training_name}</TableCell>
                <TableCell className="text-xs text-slate-500">{it.office_name} / {it.fiscal_year}年度</TableCell>
                <TableCell><Checkbox checked={it.required} onCheckedChange={(c) => upd(it, { required: !!c })} data-testid={`onbset-req-${it.id}`} /></TableCell>
                <TableCell className="text-sm text-slate-500">{it.due_days ?? "既定"}</TableCell>
                <TableCell><Checkbox checked={it.reuse_existing} onCheckedChange={(c) => upd(it, { reuse_existing: !!c })} /></TableCell>
                <TableCell><Checkbox checked={it.force_retake} onCheckedChange={(c) => upd(it, { force_retake: !!c })} /></TableCell>
                <TableCell><button onClick={() => remove(it)} className="text-rose-500" data-testid={`onbset-del-${it.id}`}><Trash2 className="w-4 h-4" /></button></TableCell>
              </TableRow>
            ))}
            {!items.length && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-6">対象研修がありません</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>入社研修に研修を追加</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>研修 *</Label>
              <Select value={form.training_id} onValueChange={(v) => setForm({ ...form, training_id: v })}>
                <SelectTrigger className="mt-1" data-testid="onbset-training-select"><SelectValue placeholder="研修を選択" /></SelectTrigger>
                <SelectContent>{candidates.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}（{c.office_name}/{c.fiscal_year}）</SelectItem>)}</SelectContent>
              </Select>
              {!candidates.length && <p className="text-xs text-slate-400 mt-1">追加できる研修がありません</p>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="flex items-center gap-2 text-sm mt-1"><Checkbox checked={form.required} onCheckedChange={(c) => setForm({ ...form, required: !!c })} />必須</label>
              <div><Label>期限日数（空=既定）</Label><Input type="number" value={form.due_days} onChange={(e) => setForm({ ...form, due_days: e.target.value })} className="mt-1" data-testid="onbset-training-due" /></div>
            </div>
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.reuse_existing} onCheckedChange={(c) => setForm({ ...form, reuse_existing: !!c })} />既存の修了記録を認める</label>
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.force_retake} onCheckedChange={(c) => setForm({ ...form, force_retake: !!c })} />入社研修として再受講を必須にする</label>
          </div>
          <DialogFooter><Button onClick={add} className="bg-blue-600 hover:bg-blue-700" data-testid="onbset-training-add">追加</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function OrientationTab() {
  const [items, setItems] = useState(null);
  const [name, setName] = useState("");
  const load = useCallback(() => api.get("/onboarding/orientation-items").then(({ data }) => setItems(data)), []);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    if (!name.trim()) return;
    try { await api.post("/onboarding/orientation-items", { name: name.trim(), description: "", order: items.length, status: "有効" }); setName(""); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const upd = async (it, patch) => { try { await api.put(`/onboarding/orientation-items/${it.id}`, { ...it, ...patch }); load(); } catch (e) { toast.error(apiErr(e)); } };
  const remove = async (it) => { if (!window.confirm(`「${it.name}」を削除しますか？`)) return; try { await api.delete(`/onboarding/orientation-items/${it.id}`); load(); } catch (e) { toast.error(apiErr(e)); } };
  if (!items) return <Loading />;
  return (
    <div className="max-w-2xl">
      <div className="rounded-lg bg-blue-50 text-blue-800 text-sm p-3 mb-3">各項目に「説明文」を登録すると、職員の本人確認時にポップアップで説明が表示され、内容を読んでから「確認しました」を押す運用になります。</div>
      <div className="flex gap-2 mb-3">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="新しいオリエン項目名" data-testid="ori-item-name" onKeyDown={(e) => e.key === "Enter" && add()} />
        <Button className="bg-blue-600 hover:bg-blue-700" onClick={add} data-testid="ori-item-add"><Plus className="w-4 h-4 mr-1" />追加</Button>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
        {items.map((it) => (
          <div key={it.id} className="px-4 py-3 space-y-2" data-testid={`ori-item-${it.id}`}>
            <div className="flex items-center gap-3">
              <GripVertical className="w-4 h-4 text-slate-300 shrink-0" />
              <Input defaultValue={it.name} onBlur={(e) => e.target.value !== it.name && upd(it, { name: e.target.value })} className="h-8 flex-1" data-testid={`ori-item-name-${it.id}`} />
              <Select value={it.status} onValueChange={(v) => upd(it, { status: v })}>
                <SelectTrigger className="h-8 w-24"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="有効">有効</SelectItem><SelectItem value="停止">停止</SelectItem></SelectContent>
              </Select>
              <button onClick={() => remove(it)} className="text-rose-500 shrink-0"><Trash2 className="w-4 h-4" /></button>
            </div>
            <Textarea defaultValue={it.description || ""} onBlur={(e) => (e.target.value !== (it.description || "")) && upd(it, { description: e.target.value })} placeholder="確認時にポップアップ表示する説明文（例：非常口・避難経路・AED・鍵の保管場所、確認手順など）" rows={2} className="text-sm" data-testid={`ori-item-desc-${it.id}`} />
          </div>
        ))}
        {!items.length && <div className="px-4 py-6 text-center text-slate-400 text-sm">オリエン項目がありません</div>}
      </div>
    </div>
  );
}

function AiTab() {
  const [form, setForm] = useState({ service_type: "", job_type: "", employment_type: "" });
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post("/onboarding/ai/recommend", form); setRes(data); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="max-w-2xl space-y-4">
      <div className="grid sm:grid-cols-3 gap-3">
        <div><Label>サービス種別</Label><Input value={form.service_type} onChange={(e) => setForm({ ...form, service_type: e.target.value })} placeholder="就労継続支援B型 等" className="mt-1" data-testid="ai-service" /></div>
        <div><Label>職種</Label><Input value={form.job_type} onChange={(e) => setForm({ ...form, job_type: e.target.value })} placeholder="生活支援員 等" className="mt-1" data-testid="ai-job" /></div>
        <div><Label>雇用形態</Label><Input value={form.employment_type} onChange={(e) => setForm({ ...form, employment_type: e.target.value })} placeholder="常勤 等" className="mt-1" data-testid="ai-emp" /></div>
      </div>
      <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={run} disabled={busy} data-testid="ai-run">{busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Sparkles className="w-4 h-4 mr-1" />}提案を生成</Button>
      {res && (
        <div className="space-y-3" data-testid="ai-result">
          <div className="rounded-lg bg-amber-50 text-amber-800 text-sm px-3 py-2 font-medium">{res.notice}</div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="font-medium text-slate-700 mb-2">推奨研修</div>
            <ul className="space-y-1.5 text-sm">
              {res.data.recommended_trainings.map((r, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className={`text-xs px-1.5 py-0.5 rounded shrink-0 ${r.required ? "bg-rose-50 text-rose-600" : "bg-slate-100 text-slate-500"}`}>{r.required ? "必須" : "任意"}</span>
                  <span><b className="text-slate-800">{r.topic}</b> — <span className="text-slate-500">{r.purpose}</span>{r.matched_training && <span className="text-green-600 text-xs">（既存研修あり: {r.matched_training.training_name}）</span>}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="font-medium text-slate-700 mb-2">推奨オリエン項目</div>
            <div className="flex flex-wrap gap-1.5">{res.data.orientation_items.map((o, i) => <span key={i} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{o}</span>)}</div>
          </div>
        </div>
      )}
    </div>
  );
}

const STD_TOPICS = ["法人・事業所の基本方針", "障がい福祉サービスの基礎", "職員倫理・権利擁護", "虐待防止", "身体拘束等の適正化", "個人情報保護・秘密保持", "感染症対策", "BCP基礎", "非常災害・防災", "事故防止・事故発生時対応", "ヒヤリハット", "苦情対応", "ハラスメント防止", "支援記録の基本", "緊急時対応", "事業所独自ルール・業務説明"];

function TemplateEditor({ initial, onClose, onSaved }) {
  const isNew = !initial?.id;
  const [name, setName] = useState(initial?.name || "");
  const [topics, setTopics] = useState(initial?.training_topics?.length ? initial.training_topics.map((t) => ({ ...t })) : []);
  const [oris, setOris] = useState(initial?.orientation_items ? [...initial.orientation_items] : []);
  const [busy, setBusy] = useState(false);
  const [stdSel, setStdSel] = useState("");

  const save = async () => {
    if (!name.trim()) { toast.error("テンプレート名を入力してください"); return; }
    setBusy(true);
    const payload = { name: name.trim(), training_topics: topics.filter((t) => t.topic.trim()), orientation_items: oris.filter((o) => o.trim()) };
    try {
      if (isNew) await api.post("/onboarding/templates", payload);
      else await api.put(`/onboarding/templates/${initial.id}`, payload);
      toast.success("テンプレートを保存しました"); onSaved();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
      <DialogHeader><DialogTitle>{isNew ? "テンプレートを作成" : "テンプレートを編集"}</DialogTitle></DialogHeader>
      <div className="space-y-4">
        <div><Label>テンプレート名 *</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1" placeholder="就労継続支援・就労移行 等" data-testid="tpl-edit-name" /></div>

        <div>
          <div className="flex items-center justify-between">
            <Label>対象研修（トピック）</Label>
            <div className="flex items-center gap-2">
              <Select value={stdSel} onValueChange={(v) => { if (v && !topics.some((t) => t.topic === v)) setTopics([...topics, { topic: v, required: STD_TOPICS.indexOf(v) >= 0 && ["虐待防止", "身体拘束等の適正化", "個人情報保護・秘密保持", "感染症対策", "非常災害・防災", "事故防止・事故発生時対応"].includes(v) }]); setStdSel(""); }}>
                <SelectTrigger className="h-8 w-40 text-xs" data-testid="tpl-edit-std"><SelectValue placeholder="標準から追加" /></SelectTrigger>
                <SelectContent>{STD_TOPICS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
              <Button size="sm" variant="outline" className="h-8" onClick={() => setTopics([...topics, { topic: "", required: false }])} data-testid="tpl-edit-add-topic"><Plus className="w-4 h-4" /></Button>
            </div>
          </div>
          <div className="mt-2 space-y-1.5">
            {topics.map((t, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`tpl-edit-topic-${i}`}>
                <Input value={t.topic} onChange={(e) => setTopics(topics.map((x, j) => j === i ? { ...x, topic: e.target.value } : x))} className="h-8 flex-1" placeholder="研修トピック名" />
                <label className="flex items-center gap-1 text-xs text-slate-500 shrink-0"><Checkbox checked={t.required} onCheckedChange={(c) => setTopics(topics.map((x, j) => j === i ? { ...x, required: !!c } : x))} />必須</label>
                <button onClick={() => setTopics(topics.filter((_, j) => j !== i))} className="text-rose-500"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            {!topics.length && <p className="text-xs text-slate-400">研修トピックを追加してください（適用時に既存研修名と自動マッチします）</p>}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between">
            <Label>現場オリエンテーション項目</Label>
            <Button size="sm" variant="outline" className="h-8" onClick={() => setOris([...oris, ""])} data-testid="tpl-edit-add-ori"><Plus className="w-4 h-4" /></Button>
          </div>
          <div className="mt-2 space-y-1.5">
            {oris.map((o, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`tpl-edit-ori-${i}`}>
                <Input value={o} onChange={(e) => setOris(oris.map((x, j) => j === i ? e.target.value : x))} className="h-8 flex-1" placeholder="オリエン項目名" />
                <button onClick={() => setOris(oris.filter((_, j) => j !== i))} className="text-rose-500"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            {!oris.length && <p className="text-xs text-slate-400">オリエン項目を追加してください</p>}
          </div>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>キャンセル</Button>
        <Button onClick={save} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="tpl-edit-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "保存"}</Button>
      </DialogFooter>
    </DialogContent>
  );
}

function TemplatesTab() {
  const [tpls, setTpls] = useState(null);
  const [busy, setBusy] = useState("");
  const [open, setOpen] = useState("");
  const [editing, setEditing] = useState(undefined); // undefined=closed, null=new, obj=edit
  const load = useCallback(() => api.get("/onboarding/templates").then(({ data }) => setTpls(data)), []);
  useEffect(() => { load(); }, [load]);

  const apply = async (t) => {
    if (!window.confirm(`「${t.name}」の内容を適用しますか？（既存の対象研修・オリエン項目に不足分のみ追加します）`)) return;
    setBusy(t.id);
    try {
      const { data } = await api.post(`/onboarding/templates/${t.id}/apply`);
      toast.success(`適用しました：研修+${data.added_trainings}／オリエン+${data.added_orientation}${data.unmatched_topics.length ? `（未マッチ${data.unmatched_topics.length}件）` : ""}`);
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };
  const remove = async (t) => {
    if (!window.confirm(`テンプレート「${t.name}」を削除しますか？`)) return;
    try { await api.delete(`/onboarding/templates/${t.id}`); toast.success("削除しました"); load(); } catch (e) { toast.error(apiErr(e)); }
  };
  const reset = async () => {
    if (!window.confirm("すべてのテンプレートを初期状態（標準6種）に戻しますか？現在のカスタマイズは失われます。")) return;
    try { await api.post("/onboarding/templates/reset"); toast.success("初期化しました"); load(); } catch (e) { toast.error(apiErr(e)); }
  };

  if (!tpls) return <Loading />;
  return (
    <div className="space-y-4 max-w-3xl">
      <div className="rounded-lg bg-blue-50 text-blue-800 text-sm p-3">サービス種別ごとの「対象研修＋現場オリエン項目」テンプレートを法人ごとに編集・保存できます。適用時に研修名を自動マッチし、不足分のみ追加します（既存データは上書きしません）。</div>
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="outline" onClick={reset} data-testid="tpl-reset">既定に戻す</Button>
        <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => setEditing(null)} data-testid="tpl-new"><Plus className="w-4 h-4 mr-1" />テンプレートを作成</Button>
      </div>
      {tpls.map((t) => (
        <div key={t.id} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`tpl-${t.id}`}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="font-semibold text-slate-800 flex items-center gap-2"><Layers className="w-4 h-4 text-blue-600" />{t.name}</div>
              <div className="text-xs text-slate-500 mt-0.5">研修 {t.match_count}/{t.topics.length} マッチ・現場オリエン {t.orientation_items.length}項目（新規 {t.orientation_new.length}）</div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" variant="outline" onClick={() => setOpen(open === t.id ? "" : t.id)} data-testid={`tpl-preview-${t.id}`}>{open === t.id ? "閉じる" : "内容を見る"}</Button>
              <Button size="sm" variant="outline" onClick={() => setEditing(t)} data-testid={`tpl-edit-${t.id}`}>編集</Button>
              <Button size="sm" variant="outline" className="text-rose-500" onClick={() => remove(t)} data-testid={`tpl-del-${t.id}`}><Trash2 className="w-4 h-4" /></Button>
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700" disabled={busy === t.id} onClick={() => apply(t)} data-testid={`tpl-apply-${t.id}`}>{busy === t.id ? <Loader2 className="w-4 h-4 animate-spin" /> : "この内容を適用"}</Button>
            </div>
          </div>
          {open === t.id && (
            <div className="mt-3 grid sm:grid-cols-2 gap-4 text-sm border-t border-slate-100 pt-3">
              <div>
                <div className="font-medium text-slate-600 mb-1">対象研修</div>
                <ul className="space-y-1">
                  {t.topics.map((x, i) => (
                    <li key={i} className="flex items-center gap-1.5">
                      {x.matched ? <CheckCircle2 className="w-3.5 h-3.5 text-green-500 shrink-0" /> : <XCircle className="w-3.5 h-3.5 text-slate-300 shrink-0" />}
                      <span className={x.matched ? "text-slate-700" : "text-slate-400"}>{x.topic}</span>
                      {x.required && <span className="text-[10px] text-rose-500">必須</span>}
                      {x.already && <span className="text-[10px] text-blue-500">追加済</span>}
                    </li>
                  ))}
                  {!t.topics.length && <li className="text-slate-400 text-xs">トピック未設定</li>}
                </ul>
              </div>
              <div>
                <div className="font-medium text-slate-600 mb-1">現場オリエン項目</div>
                <div className="flex flex-wrap gap-1.5">{t.orientation_items.map((o, i) => <span key={i} className={`text-xs px-2 py-0.5 rounded-full ${t.orientation_new.includes(o) ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-500"}`}>{o}</span>)}</div>
              </div>
            </div>
          )}
        </div>
      ))}

      <Dialog open={editing !== undefined} onOpenChange={(o) => !o && setEditing(undefined)}>
        {editing !== undefined && <TemplateEditor initial={editing} onClose={() => setEditing(undefined)} onSaved={() => { setEditing(undefined); load(); }} />}
      </Dialog>
    </div>
  );
}

export default function OnboardingSettings() {
  const navigate = useNavigate();
  return (
    <div>
      <Button variant="ghost" onClick={() => navigate("/onboarding")} className="mb-2" data-testid="onbset-back"><ArrowLeft className="w-4 h-4 mr-1" />入社研修</Button>
      <PageHeader title="入社研修設定" subtitle="機能設定・対象研修・現場オリエン項目・AI提案" />
      <Tabs defaultValue="basic">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="basic" data-testid="onbset-tab-basic">基本設定</TabsTrigger>
          <TabsTrigger value="templates" data-testid="onbset-tab-templates">テンプレート</TabsTrigger>
          <TabsTrigger value="trainings" data-testid="onbset-tab-trainings">対象研修</TabsTrigger>
          <TabsTrigger value="orientation" data-testid="onbset-tab-orientation">現場オリエン項目</TabsTrigger>
          <TabsTrigger value="ai" data-testid="onbset-tab-ai">AI提案</TabsTrigger>
        </TabsList>
        <TabsContent value="basic"><BasicTab /></TabsContent>
        <TabsContent value="templates"><TemplatesTab /></TabsContent>
        <TabsContent value="trainings"><TrainingSetTab /></TabsContent>
        <TabsContent value="orientation"><OrientationTab /></TabsContent>
        <TabsContent value="ai"><AiTab /></TabsContent>
      </Tabs>
    </div>
  );
}
